// src/pages/talleres/editar/useTallerEditar.js
import { useEffect, useState } from "react";
import { message, Form } from "antd";
import dayjs from "dayjs";
import Recintos from "../../../../../service/Recintos";
import Talleres from "../../../../../service/Talleres";
import { CAMPOS_VALORES_EXT } from "./utils/tallerUtils";

export default function useTallerEditar(id) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msgApi, contextHolder] = message.useMessage();

  const [data, setData] = useState(null);
  const [recintos, setRecintos] = useState([]);
  const [recintosLoading, setRecintosLoading] = useState(false);

  const [formInfo] = Form.useForm();
  const [formFechas] = Form.useForm();
  const [formPlanes] = Form.useForm();

  const [planActive, setPlanActive] = useState({ 
    mensual: false, 
    trimestral: false, 
    semestral: false, 
    anual: false 
  });

  const [usuariosAutorizados, setUsuariosAutorizados] = useState([]);
  const [horarios, setHorarios] = useState([]);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        setRecintosLoading(true);
        
        const [recintosRes, tallerRes] = await Promise.all([
          Recintos.List({ limit: 1000 }), 
          Talleres.getById(id)
        ]);
        
        setRecintos((recintosRes.data || recintosRes || []).map(r => ({ 
          label: r.nombre, 
          value: r._id 
        })));
        setRecintosLoading(false);

        const t = tallerRes;
        setData(t);

        // Cargar usuarios autorizados
        setUsuariosAutorizados(
          Array.isArray(t.usuariosAutorizados) ? t.usuariosAutorizados : []
        );

        // 🔧 FIX: Extraer correctamente el ID del recinto
        const recintoId = typeof t.recinto === 'string' 
          ? t.recinto 
          : t.recinto?._id || null;

        console.log('🔍 Recinto del taller:', t.recinto);
        console.log('🔍 Recinto ID extraído:', recintoId);

        // Cargar info básica
        const tipoTallerValue = t.tipoTaller || 'libre';
        
        formInfo.setFieldsValue({
          nombre: t.nombre,
          tipoActividad: t.tipoActividad,
          recinto: recintoId, // 🔧 FIX: Usar el ID extraído
          descripcion: t.descripcion,
          tipoDuracionSuscripcion: t.tipoDuracionSuscripcion || 'mes_calendario',
          nivelIntensidad: t.nivelIntensidad || 'moderada',
          edadMin: t.edadMin,
          edadMax: t.edadMax ?? null,
          liberarCuposPorBaja: t.liberarCuposPorBaja ?? true,
          soloTarjetaVecino: t.soloTarjetaVecino || false,
          requiereMatricula: t.requiereMatricula || false,
          tieneIVA: t.tieneIVA || false,
          accesoPiscina: t.accesoPiscina || false,
          avisoVencimiento: t.avisoVencimiento || false,
          tipoTaller: tipoTallerValue,
        });
        
        // Cargar fechas de inscripción CON HORA y fechas de actividades
        formFechas.setFieldsValue({
          inscripcionInicio: t.inscripcionInicio ? dayjs(t.inscripcionInicio) : undefined,
          inscripcionFin: t.inscripcionFin ? dayjs(t.inscripcionFin) : undefined,
          fechasActividades: t.actividadesInicio && t.actividadesFin
            ? [dayjs(t.actividadesInicio), dayjs(t.actividadesFin)]
            : undefined,
        });

        // Manejar modalidades de pago
        const esGratuito = t.esGratuito || false;
        const actives = { mensual: false, trimestral: false, semestral: false, anual: false };
        const valoresInit = { mensual: {}, trimestral: {}, semestral: {}, anual: {} };
        
        if (!esGratuito && Array.isArray(t.modalidadPago)) {
          t.modalidadPago.forEach(mp => {
            actives[mp.plan] = true;
            CAMPOS_VALORES_EXT.forEach(c => { 
              if (mp[c.key] != null) {
                valoresInit[mp.plan][c.key] = mp[c.key]; 
              }
            });
          });
        }
        
        setPlanActive(actives);
        formPlanes.setFieldsValue({ valores: valoresInit });

        // Cargar horarios con normalización
        const h = (() => {
          const raw = t.horarios || [];
          
          if (raw.every(x => Array.isArray(x.bloques))) {
            return raw.map(x => ({
              key: x._id || `${x.nombre || 'horario'}`,
              _id: x._id,
              nombre: x.nombre,
              bloques: x.bloques || [],
              cupos: x.cupos ?? null,
              cuposOcupados: x.cuposOcupados || 0,
              cuposReservados: x.cuposReservados || 0,
              cuposIlimitados: x.cuposIlimitados || false,
              taller: t._id,
            }));
          }

          if (raw.every(x => Array.isArray(x.dias))) {
            return raw.map(x => ({
              key: x._id || `${x.inicio}-${x.fin}-${(x.dias || []).join(",")}`,
              _id: x._id,
              dias: x.dias || [],
              inicio: x.inicio,
              fin: x.fin,
              cupos: x.cupos ?? null,
              cuposOcupados: x.cuposOcupados || 0,
              cuposReservados: x.cuposReservados || 0,
              taller: t._id,
            }));
          }
          
          const grupos = {};
          raw.forEach(hh => {
            const inicio = hh.inicio || hh.horaInicio;
            const fin = hh.fin || hh.horaFin;
            const cupos = hh.cupos ?? null;
            const cuposOcupados  = hh.cuposOcupados  || 0;
            const cuposReservados = hh.cuposReservados || 0;
            const dia = hh.diaSemanaNombre || hh.dia || "lunes";
            const k = `${inicio}-${fin}-${cupos ?? ""}`;

            if (!grupos[k]) {
              grupos[k] = {
                dias: [],
                inicio,
                fin,
                cupos,
                cuposOcupados,
                cuposReservados,
                taller: t._id
              };
            }
            grupos[k].dias.push(dia);
          });
          
          return Object.values(grupos).map(x => ({
            key: `${x.inicio}-${x.fin}-${x.dias.join(",")}`,
            ...x,
          }));
        })();
        
        setHorarios(h);

      } catch (error) {
        console.error('❌ Error cargando taller:', error);
        msgApi.error("No se pudo cargar el taller");
      } finally {
        setLoading(false);
      }
    };
    
    load();
  }, [id]);

  return {
    loading, 
    saving, 
    setSaving, 
    msgApi, 
    contextHolder,
    data, 
    setData,
    recintos, 
    recintosLoading,
    formInfo, 
    formFechas, 
    formPlanes,
    planActive, 
    setPlanActive,
    horarios, 
    setHorarios,
    usuariosAutorizados,
    setUsuariosAutorizados,
  };
}
